import { Injectable } from '@angular/core';
import { PlatformAuthModalPanel } from '../enums/platform-auth-modal-panel.enum';

export interface PlatformAuthModalOptions {
  panel?: PlatformAuthModalPanel;
  /**
   * Post-login navigation target.
   * - `null` → dashboard
   * - URL string → navigate there when different from current
   * - omit → stay on the current jobboard page
   */
  redirectUrl?: string | null;
  email?: string | null;
  nonce?: string | null;
  registerSuccess?: boolean;
  isPasswordReset?: boolean;
  /** Runs once after a successful login (e.g. pending bookmark). */
  afterLogin?: () => void;
}

type AuthModalOpenFn = (options: PlatformAuthModalOptions) => void;

@Injectable({
  providedIn: 'root',
})
export class PlatformJobboardLoginModalService {
  private openFn: AuthModalOpenFn | null = null;
  private pending: PlatformAuthModalOptions | null = null;
  private afterLoginFn: (() => void) | null = null;

  /**
   * Registers the jobboard auth modal opener and flushes a pending open request.
   * @param openFn - Function that opens the modal with the given options
   */
  register(openFn: AuthModalOpenFn): void {
    this.openFn = openFn;
    if (this.pending) {
      const pending = this.pending;
      this.pending = null;
      openFn(pending);
    }
  }

  /**
   * Opens the registered jobboard auth modal when available.
   * @param options - Panel and optional context (redirect, email, nonce)
   */
  open(options: PlatformAuthModalOptions | PlatformAuthModalPanel = {}): void {
    const normalized: PlatformAuthModalOptions =
      typeof options === 'string' ? { panel: options } : { ...options };
    if (!normalized.panel) {
      normalized.panel = PlatformAuthModalPanel.LOGIN;
    }
    this.afterLoginFn = normalized.afterLogin ?? null;
    if (this.openFn) {
      this.openFn(normalized);
      return;
    }
    this.pending = normalized;
  }

  /**
   * Runs and clears a pending post-login action.
   */
  runAfterLogin(): void {
    const afterLogin = this.afterLoginFn;
    this.afterLoginFn = null;
    afterLogin?.();
  }

  /**
   * Clears a pending post-login action without running it.
   */
  clearAfterLogin(): void {
    this.afterLoginFn = null;
  }
}
